import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { flushAll } from "@/composables/pending-flush";
import { editChannel, syncChannel } from "@/lib/broadcast-channels";
import { ClusterRole, useUserManager } from "@/stores/user-store";

// Only these roles may enter edit mode for stek content.
const EDIT_ROLES = [
    ClusterRole.USER,
    ClusterRole.STEK_MANAGER,
    ClusterRole.ORGANIZATION_MANAGER,
];

export const useEditStore = defineStore("edit", () => {
    const { currentOrganizationRoles } = useUserManager();
    const route = useRoute();
    const canEditInternal = ref(true);
    // Viewing a historical version (a real version SHA in the route, not the
    // live "current") is read-only.
    const isViewingHistoryVersion = computed(() => {
        const version = route?.params?.version;
        return (
            typeof version === "string" &&
            version.length > 0 &&
            version !== "current"
        );
    });
    const canEdit = computed(() => {
        return (
            canEditInternal.value &&
            currentOrganizationRoles.value.some((r) =>
                EDIT_ROLES.includes(r)
            ) &&
            !isViewingHistoryVersion.value
        );
    });
    const isEditing = ref(false);
    const isViewOnly = computed(
        () =>
            !currentOrganizationRoles.value.some((r) => EDIT_ROLES.includes(r))
    );

    const startEditing = async () => {
        if (canEdit.value) {
            isEditing.value = true;
            editChannel.postMessage("start");
        }
    };
    const stopEditing = () => {
        // Force any throttled per-entity saves (task/gateway/event/flow/... stores)
        // to fire immediately rather than waiting out their trailing-edge delay
        // (FILE_SYNC_THROTTLE_MS). Those stores previously only flushed on SPA
        // route changes (see router/index.ts), so leaving edit mode and then
        // immediately hard-navigating/reloading — as opposed to clicking an
        // in-app link — could abort a still-pending save and silently drop the
        // edit.
        //
        // Ahead of the flag, not after it: a flush that has to go through
        // edit-mode-only machinery is dead once `isEditing` is false. The BPMN
        // element drawer commits a typed-but-unblurred name through the
        // modeler, which Definition.vue only attaches while editing — flushing
        // afterwards found no modeler and dropped the rename. No registrant
        // reads `isEditing` itself, so running them a tick earlier is
        // otherwise inert.
        flushAll();
        isEditing.value = false;
    };

    watch(isViewOnly, (newValue) => {
        if (newValue) {
            stopEditing();
        }
    });

    // Leaving edit mode when navigating into a historical version keeps the
    // page read-only (mirrors release viewing).
    watch(
        isViewingHistoryVersion,
        (viewingHistory) => {
            if (viewingHistory) {
                stopEditing();
            }
        },
        { immediate: true }
    );

    editChannel.addEventListener("message", (event) => {
        if (event.data === "start") {
            stopEditing();
        }
    });

    syncChannel.addEventListener("message", ({ data: { message } }) => {
        if (message === "queued") {
            canEditInternal.value = false;
        } else if (message === "synced") {
            canEditInternal.value = true;
        }
    });

    return { canEdit, isEditing, startEditing, stopEditing, isViewOnly };
});

export const useEditManager = () => storeToRefs(useEditStore());
